import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { AllieLoading } from '@allie/app-ui-lib';
import { GET_LOGGED_USER, GUEST_LOGIN } from '../data';
import { PermissionAction, PermissionSubject } from '../helpers/enums/permission.enums';
import { GUEST_UID, useAllieRouting, useAllieStorage, useAppTranslations } from '../hooks';
import { OpenLoginProps, OpenLoginType } from '../App.types';
import { config, LoginType, UserDto } from '../helpers';
import { SessionEndType, TrackingEvents } from '../helpers/tracking-events';
import { UtilsContext } from './UtilsProvider';

const WELCOME_PAGE = '/welcome';
export interface Permission {
  subject: PermissionSubject;
  actions: [PermissionAction];
}
export interface LoggedUser {
  id: number;
  name: string;
  avatar: string;
  email: string;
  forcePasswordChange: boolean;
  supportHash: string;
  phone: string;
  role: string;
  lastCredentialType: string;
  preferedLanguage: string;
}

export type CompactPermissions = Partial<
  Record<PermissionSubject, Record<PermissionAction, boolean>>
>;

export const AuthContext = createContext<{
  permissions?: CompactPermissions;
  onLoginSuccessful?: (
    permissions: Permission[],
    shouldChangeUser?: boolean,
    shouldRestoreLastPage?: boolean,
    cb?: () => void
  ) => void;
  setUser?: (user: UserDto, cb?: () => void) => void;
  patchLoggedUser?: (user: Partial<UserDto>, cb?: () => void) => void;
  loggedUser?: LoggedUser;
  tryGuestLogin?: (
    shouldChangeUser: boolean,
    shouldRestoreLastPage?: boolean,
    isExternalCheck?: boolean
  ) => Promise<unknown>;
  openLogin?: OpenLoginType;
  openLoginFn?: OpenLoginProps;
  guestModeLoading?: boolean;
  guestModeFailed?: boolean;
  isChangingUser?: boolean;
  handleAfterLogin?: any;
}>({});

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [getLoggedUserQuery] = useLazyQuery(GET_LOGGED_USER, {
    fetchPolicy: 'network-only',
  });
  const { GUEST_MODE_LOADING_MESSAGE } = useAppTranslations();
  const [guestLogin] = useMutation(GUEST_LOGIN);
  const { restoreLastVisitedPage, goToLogin, resolvePage } = useAllieRouting();
  const [openLoginFn, setOpenLoginFn] = useState<OpenLoginProps>();
  const timeoutRef = useRef<NodeJS.Timeout>();
  const {
    setPermissions,
    setLoggedUser,
    setAutoGuestEnabled,
    getPermissions,
    getLoggedUser,
    getSessionExpiresAt,
    getAutoGuestEnabled,
    clearAuthData,
    getDeeplink,
  } = useAllieStorage();
  const [guestModeLoading, setGuestModeLoading] = useState(false);
  const [guestModeFailed, setGuestModeFailed] = useState(false);
  const [loggedUserStatus, setLoggedUserStatus] = useState({ change: false });
  const [isChangingUser, setIsChangingUser] = useState(false);
  const { tracker, company } = useContext(UtilsContext);

  const trackLogin = (user: LoggedUser, previousUser: LoggedUser | null) => {
    if (previousUser) {
      tracker?.track(TrackingEvents.LOGIN.login_user_session_ended, {
        user_name: previousUser.name,
        user_role: previousUser.role,
        user_client: company?.name || '',
        session_end_type: SessionEndType.ACCOUNT_SWITCH,
      });
    }
    tracker?.track(TrackingEvents.LOGIN.login_user_loggedin, {
      user_name: user.name,
      user_role: user.role,
      user_client: company?.name || '',
      login_type: LoginType.PROACTIVE,
    });
  };

  const openLogin: OpenLoginType = useCallback(
    ({
      onDismiss,
      onSuccess,
      onError,
      shouldRestoreLastPage = false,
      showMessageError = false,
    }) => {
      setOpenLoginFn({
        onDismiss,
        onSuccess,
        onError,
        shouldRestoreLastPage,
        showMessageError,
      });
    },
    []
  );

  const isGuest = () => getLoggedUser().id === GUEST_UID;

  const handleAfterLogin = useCallback(
    (shouldRestoreLastPage: boolean, userId: number, cb?: any, ignoreDeeplink = false) => {
      const deeplink = getDeeplink();

      if (cb) cb();

      // localStorage should already be updated at this point
      if (deeplink && !ignoreDeeplink) {
        document.location.href = resolvePage(deeplink);
      } else {
        if (shouldRestoreLastPage) {
          restoreLastVisitedPage(userId);
        }
      }
    },
    [restoreLastVisitedPage]
  );

  const changeUser = useCallback(
    (shouldRestoreLastPage = true, cb?: () => void) => {
      // Set flag to prevent concurrent changeUser calls
      // Set localStorage FIRST to ensure microfrontends see it immediately
      localStorage.setItem('isChangingUser', 'true');
      setIsChangingUser(true);

      getLoggedUserQuery()
        .then((userResponse: any) => {
          const {
            data: { loggedUser },
          } = userResponse;

          const previousUser = !isGuest() ? getLoggedUser() : null;
          
          // Force immediate localStorage update
          setLoggedUser(loggedUser);
          setLoggedUserStatus({ change: true });
          setAutoGuestEnabled(true);
          
          // Force localStorage persistence by reading it back immediately
          const updatedUser = getLoggedUser();
          
          // Double check that the user was actually saved
          if (updatedUser.id !== loggedUser.id) {
            // Force another save
            setLoggedUser(loggedUser);
          }

          if (loggedUser.id !== GUEST_UID) trackLogin(loggedUser, previousUser);

          if (loggedUser.forcePasswordChange) {
            // Use Promise to ensure localStorage is written before redirect
            Promise.resolve().then(() => {
              setIsChangingUser(false);
              localStorage.removeItem('isChangingUser');
              document.location.href = WELCOME_PAGE;
            });
          } else {
            // Use Promise to ensure localStorage is written before any redirection
            Promise.resolve().then(() => {
              setIsChangingUser(false);
              localStorage.removeItem('isChangingUser');
              handleAfterLogin(shouldRestoreLastPage, loggedUser.id, cb);
            });
          }
        })
        .catch((error) => {
          // Fallback: Set guest user manually if query fails
          const guestUser = { id: GUEST_UID, name: 'GUEST' };
          
          setLoggedUser(guestUser);
          setLoggedUserStatus({ change: true });
          setAutoGuestEnabled(true);
          
          // Verify that the guest user was actually saved
          const updatedUser = getLoggedUser();
          if (updatedUser.id !== guestUser.id) {
            // Force another save if first attempt failed
            setLoggedUser(guestUser);
          }
          
          // Use Promise to ensure localStorage is written before callback
          Promise.resolve().then(() => {
            setIsChangingUser(false);
            localStorage.removeItem('isChangingUser');
            if (cb) cb();
          });
        });
    },
    [
      getLoggedUserQuery,
      setLoggedUser,
      setLoggedUserStatus,
      setAutoGuestEnabled,
      restoreLastVisitedPage,
      trackLogin,
    ]
  );

  const parsePermissions = (permissions: Permission[]) => {
    return permissions.reduce(
      (acum, p) => ({
        ...acum,
        [p.subject]: p.actions.reduce((sub, a) => ({ ...sub, [a]: true }), {}),
      }),
      {}
    );
  };

  const setUser = useCallback((user: UserDto, cb?: () => void) => {
    const { permissions, ...userInfo } = user;

    setPermissions(parsePermissions(permissions));
    setLoggedUser(userInfo);
    setLoggedUserStatus({ change: true });
    setAutoGuestEnabled(true);

    cb?.();
  }, []);

  const patchLoggedUser = useCallback(
    (user: Partial<UserDto>, cb?: () => void) => {
      const newLoggedUser = { ...getLoggedUser(), ...user };
      setLoggedUser(newLoggedUser);
      cb?.();
    },
    [getLoggedUser()]
  );

  const onLoginSuccessful = useCallback(
    (
      permissions: Permission[],
      shouldChangeUser = true,
      shouldRestoreLastPage = true,
      cb?: () => void
    ) => {
      setPermissions(parsePermissions(permissions));

      if (shouldChangeUser) changeUser(shouldRestoreLastPage, cb);
    },
    [changeUser, setPermissions]
  );

  const tryGuestLogin = useCallback(
    (
      shouldChangeUser: boolean,
      shouldRestoreLastPage = true,
      isExternalCheck = false
    ): Promise<void> => {
      return new Promise<void>((res, rej) => {
        if (shouldChangeUser) setGuestModeLoading(true);
        guestLogin({ variables: { isExternalCheck } })
          .then((permissions) => {
            if (onLoginSuccessful && !isExternalCheck) {
              try {
                onLoginSuccessful(
                  permissions.data.guestLogin,
                  shouldChangeUser,
                  shouldRestoreLastPage
                );
                setGuestModeFailed(false);
              } catch (error) {
                // Fallback: Set guest user manually if onLoginSuccessful fails
                if (shouldChangeUser) {
                  const guestUser = { id: GUEST_UID, name: 'GUEST' };
                  setLoggedUser(guestUser);
                  setLoggedUserStatus({ change: true });
                  setAutoGuestEnabled(true);
                }
                setGuestModeFailed(false);
              }
            }
            res();
          })
          .catch((error) => {
            if (!isExternalCheck) {
              clearAuthData();
              setAutoGuestEnabled(false);
              goToLogin();
              setGuestModeFailed(true);
            }
            rej();
          })
          .finally(() => setGuestModeLoading(false));
      });
    },
    [guestLogin, onLoginSuccessful, clearAuthData, setAutoGuestEnabled, goToLogin]
  );

  const loggedUser = useMemo(() => getLoggedUser(), [getLoggedUser()]);

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      !isGuest &&
        tracker?.track(TrackingEvents.LOGIN.login_user_session_ended, {
          user_name: loggedUser?.name,
          user_client: company?.name,
          user_role: loggedUser?.role,
          session_end_type: SessionEndType.AUTOMATIC,
        });
      if (getAutoGuestEnabled() && !config.SECURE) {
        tryGuestLogin(!isGuest());
      }
    }, getSessionExpiresAt() - Date.now());
  }, [getSessionExpiresAt()]);

  const getContextValue = useMemo(
    () => ({
      permissions: getPermissions(),
      onLoginSuccessful,
      tryGuestLogin,
      openLogin,
      openLoginFn,
      guestModeLoading,
      guestModeFailed,
      isChangingUser,
      handleAfterLogin,
      setUser,
      patchLoggedUser,
      loggedUser,
    }),
    [
      getPermissions,
      getLoggedUser,
      onLoginSuccessful,
      tryGuestLogin,
      loggedUserStatus,
      openLogin,
      openLoginFn,
      guestModeLoading,
      guestModeFailed,
      isChangingUser,
      handleAfterLogin,
      setUser,
      patchLoggedUser,
      loggedUser,
    ]
  );

  return (
    <AuthContext.Provider value={getContextValue}>
      <AllieLoading isOpen={guestModeLoading} message={GUEST_MODE_LOADING_MESSAGE} />
      {children}
    </AuthContext.Provider>
  );
};
